import React, { useRef } from 'react';
import { useDrag, useDrop, DragSourceMonitor, DropTargetMonitor } from 'react-dnd';
import { XYCoord } from 'dnd-core';
import { Field } from './index';
import { FIELD_TYPES } from './index';
import { ICard } from './index';

export const ItemTypes = {
    CARD: 'card',
};

export interface ICardProps<T> {
    index: number;
    card: T;
    id: string | number;
    fields: Field<any, any>[];
    moveCard: (dragIndex: number, hoverIndex: number) => void;
    onClick?: (card: T) => void;
    onDrop?: () => void;
    name?: string;
}

interface DragItem {
    index: number;
    id: string;
    type: string;
}

export const Card = (props: ICardProps<ICard>) => {
    const { id, fields, index, card, moveCard, onClick, onDrop, name } = props;
    const ref = useRef<HTMLDivElement>(null);
    const [, drop] = useDrop({
        accept: ItemTypes.CARD,
        hover(item: DragItem, monitor: DropTargetMonitor) {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;

            // Don't replace items with themselves
            if (dragIndex === hoverIndex) {
                return;
            }

            // Determine rectangle on screen
            const hoverBoundingRect = ref.current?.getBoundingClientRect();

            // Get vertical middle
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

            // Determine mouse position
            const clientOffset = monitor.getClientOffset();

            // Get pixels to the top
            const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

            // Only perform the move when the mouse has crossed half of the items height
            // When dragging downwards, only move when the cursor is below 50%
            // When dragging upwards, only move when the cursor is above 50%

            // Dragging downwards
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            // Dragging upwards
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            // Time to actually perform the action
            moveCard(dragIndex, hoverIndex);

            // Note: we're mutating the monitor item here!
            // Generally it's better to avoid mutations,
            // but it's good here for the sake of performance
            // to avoid expensive index searches.
            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag] = useDrag<any, any, any>({
        item: { type: ItemTypes.CARD, id, index },
        end: (item: { name: string } | undefined, monitor: DragSourceMonitor) => {
            if (item && onDrop) {
                onDrop();
            }
        },
        collect: (monitor: DragSourceMonitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const handleClick = () => {
        if (onClick) {
            onClick(card);
        }
    };

    const opacity = isDragging ? 0.4 : 1;

    drag(drop(ref));

    const renderFields = () => {
        const renderText = (field: Field<any, any>) => {
            return field.modifier ? field.modifier(card[field.key], card) : card[field.key];
        };

        return fields.map((field) => {
            const style: React.CSSProperties & {
                transformBox?:
                    | 'inherit'
                    | 'initial'
                    | '-moz-initial'
                    | 'revert'
                    | 'unset'
                    | 'border-box'
                    | 'fill-box'
                    | 'view-box'
                    | undefined;
            } = {};

            if (card[field.key]) {
                style.backgroundImage = `url('${card[field.key]}')`;
            }

            return field.type === FIELD_TYPES.IMAGE ? (
                <div key={field.key} className={`draggable-${field.key}`} style={style}></div>
            ) : (
                <div key={field.key} className={`draggable-${field.key}`}>
                    {renderText(field)}
                </div>
            );
        });
    };

    return (
        <div
            key={`draggable-card-${id}`}
            className="draggable-card"
            data-component-name="draggable-card"
            style={{ opacity }}
        >
            <div className={`${isDragging ? 'draggable-onDrag' : 'draggable-handler'}`} ref={ref}></div>
            <div className="draggable-item" onClick={handleClick} id={`${name}`}>
                {renderFields()}
            </div>
        </div>
    );
};
